import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { parseJwt } from '@marqvision/shared';
import { IApplicant, ISignInUp } from '~/models';
import { Auth } from '~/swagger/Auth';
import { Logger } from '../utils';
import BaseApi, { http } from './base';

const QueryKey = 'auth';
const SwaggerAuthApi = new Auth(http);

export type ISignUpRequest = ISignInUp & { privacyAgreement?: boolean } & Omit<Partial<IApplicant>, 'email'>;

interface IVerifySendRequest {
  email: string;
  language: string;
}

interface IVerifySendResponse {
  token: string;
}

interface IVerifyCheckRequest {
  email: string;
  token: string;
}

export interface IVerifyCheckResponse {
  verified: boolean;
}

interface IVerifyRequest {
  verifyToken: string;
}

interface ISignUpResponse {
  accessToken: string;
  refreshToken: string;
  userId: number;
  email: string;
  createdDate: string;
}

interface ILoginRequest {
  email: string;
  password: string;
}

type IFindVerifySendRequest = IVerifySendRequest;
type IFindVerifySendResponse = IVerifySendResponse;

type IFindVerifyCheckRequest = IVerifyCheckRequest;
type IFindVerifyCheckResponse = IVerifyCheckResponse;

type IFindVerifyRequest = IVerifyRequest;

interface IFindResetRequest {
  email: string;
  password: string;
  token: string;
}

export type ILoginResponse = ISignUpResponse;

interface IRefreshRequest {
  refreshToken: string;
}

export interface IRefreshResponse {
  accessToken: string;
}

type UserToken = {
  email: string;
};

export const getAuth = (): UserToken | undefined => {
  const data = localStorage.getItem(UserInfoLocalStorageIdentifier);
  if (data) {
    const { accessToken } = JSON.parse(data);
    if (accessToken) {
      return parseJwt<UserToken>(accessToken);
    }
  }
};

export const setAuth = (queryClient: QueryClient, partialResponse: Partial<ISignUpResponse>) => {
  const { accessToken, refreshToken, userId, email, createdDate } = partialResponse;

  queryClient.setQueryData([QueryKey], { accessToken, refreshToken });

  if (userId && email && createdDate) {
    Logger.setUser(String(userId), email, createdDate);
  }

  const authData = { ...(queryClient.getQueryData<ILoginResponse>(['auth']) || {}), ...partialResponse };
  localStorage.setItem(UserInfoLocalStorageIdentifier, JSON.stringify(authData));
  queryClient.invalidateQueries(['home', 'profile']);
};

export const clearAuth = (queryClient: QueryClient): void => {
  queryClient.setQueryData([QueryKey], null);
  queryClient.setQueryData(['home', 'profile'], null);
  localStorage.removeItem(UserInfoLocalStorageIdentifier);

  Logger.clear();
};

const UserInfoLocalStorageIdentifier = '_mqfl_ui';

class AuthApiClass {
  verifySend(request: IVerifySendRequest): Promise<AxiosResponse<IVerifySendResponse>> {
    return BaseApi.post<IVerifySendResponse>('/auth/signup/verify/send', request, undefined, false);
  }

  verifyCheck(request: IVerifyCheckRequest): Promise<AxiosResponse<IVerifyCheckResponse>> {
    return BaseApi.post<IVerifyCheckResponse>('/auth/signup/verify/check', request, undefined, false);
  }

  verify(request: IVerifyRequest): Promise<AxiosResponse<void>> {
    return BaseApi.post<void>('/auth/signup/verify', request, undefined, false);
  }

  signUp(request: ISignUpRequest): Promise<AxiosResponse<ISignUpResponse>> {
    return BaseApi.post<ISignUpResponse>('/auth/signup', request, undefined, false);
  }

  login(request: ILoginRequest): Promise<AxiosResponse<ILoginResponse>> {
    return BaseApi.post<ILoginResponse>('/auth/login', request, undefined, false);
  }

  refresh(request: IRefreshRequest): Promise<AxiosResponse<IRefreshResponse>> {
    return BaseApi.post<IRefreshResponse>('/auth/refresh', request, undefined, false);
  }

  findVerifySend(request: IFindVerifySendRequest): Promise<AxiosResponse<IFindVerifySendResponse>> {
    return BaseApi.post<IVerifySendResponse>('/auth/find/verify/send', request, undefined, false);
  }

  findVerifyCheck(request: IFindVerifyCheckRequest): Promise<AxiosResponse<IFindVerifyCheckResponse>> {
    return BaseApi.post<IVerifyCheckResponse>('/auth/find/verify/check', request, undefined, false);
  }

  findVerify(request: IFindVerifyRequest): Promise<AxiosResponse<void>> {
    return BaseApi.post<void>('/auth/find/verify', request, undefined, false);
  }

  findReset(request: IFindResetRequest): Promise<AxiosResponse<ILoginResponse>> {
    return BaseApi.post<ILoginResponse>('/auth/find/reset', request, undefined, false);
  }
}

const AuthApi = new AuthApiClass();

export default AuthApi;

/* queries */
export const useAuth = (): UseQueryResult<{ accessToken: string; refreshToken: string }, any> => {
  // TODO: explicit type def
  return useQuery([QueryKey], {
    initialData: () => {
      try {
        const { userId, email, createdDate, accessToken, refreshToken } = JSON.parse(
          localStorage.getItem(UserInfoLocalStorageIdentifier) || '{}'
        );

        if (userId && email && createdDate) {
          Logger.setUser(userId, email, createdDate);
        }

        return { accessToken, refreshToken };
      } catch (e) {
        throw new Error('Failed to load user info');
      }
    },
  }) as unknown as UseQueryResult<any, any>;
};

export const useLoginMutation = (): UseMutationResult<
  AxiosResponse<ILoginResponse>,
  unknown,
  ILoginRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(AuthApi.login, {
    onSuccess: (response) => {
      setAuth(queryClient, response.data);
      queryClient.clear();
    },
  });
};

export const useSignupMutation = (): UseMutationResult<
  AxiosResponse<ISignUpResponse>,
  unknown,
  ISignUpRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(AuthApi.signUp, {
    onSuccess: (response) => {
      setAuth(queryClient, response.data);
    },
  });
};

export const usePasswordResetMutation = (): UseMutationResult<
  AxiosResponse<ILoginResponse>,
  unknown,
  IFindResetRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(AuthApi.findReset, {
    onSuccess: (response) => {
      setAuth(queryClient, response.data);
    },
  });
};

export const useCheckSignUp = () => useMutation(SwaggerAuthApi.emailVerifyCreate);

export const useSendSignupVerify = () => useMutation(SwaggerAuthApi.signupVerifySendCreate);

export const useCheckSignupVerify = () => useMutation(SwaggerAuthApi.signupVerifyCheckCreate);
